import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { CustomDateRange } from '../../../CustomDateRange';
import { Dropdown } from '../../../Dropdown';
import { RadioButton } from '../../../RadioButton';
import { DonationTypeOption } from '../../../dataBoards/donations/models';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { DateRangeOption, IDayOption } from '../../models';
import { dayOptions } from '../../utils';
import { styleSheet } from './styles';

export const DateRangeFilterDropdown = ({
	minimumDonationDate,
	setMinimumDonationDate,
	maximumDonationDate,
	setMaximumDonationDate,
	selectedDonationType,
	setSelectedDonationType,
	isFirstDonation,
	setIsFirstDonation,
	showCustomRange = false,
	anchorClassName,
}: {
	minimumDonationDate: string;
	setMinimumDonationDate: React.Dispatch<React.SetStateAction<string>>;
	maximumDonationDate: string;
	setMaximumDonationDate: React.Dispatch<React.SetStateAction<string>>;
	selectedDonationType: DonationTypeOption;
	setSelectedDonationType: React.Dispatch<React.SetStateAction<DonationTypeOption>>;
	isFirstDonation: boolean;
	setIsFirstDonation: React.Dispatch<React.SetStateAction<boolean>>;
	showCustomRange?: boolean;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [selectedDayOption, setSelectedDayOption] = React.useState<IDayOption>(null);

	const getTypeText = () => (isFirstDonation ? 'First Donation' : selectedDonationType);

	const getDateRangeMessage = () => {
		if (!minimumDonationDate || !selectedDayOption) {
			return 'Date Range';
		}
		const key = selectedDayOption.key;
		const typeText = getTypeText();
		return `${typeText}/${DateRangeOption[key]}`;
	};
	const dateRangeMessage = getDateRangeMessage();

	const selectDateRangeOption = (newDayOption: IDayOption, ev: React.MouseEvent<HTMLButtonElement>) => {
		if (newDayOption.value === DateRangeOption.Custom) {
			setSelectedDayOption(newDayOption);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			setMinimumDonationDate(newDayOption.value);
			setIsOpen(false);
		}
	};

	React.useEffect(() => {
		if (maximumDonationDate) {
			const customDayOption = dayOptions.find(option => option.value === DateRangeOption.Custom);
			setSelectedDayOption(customDayOption);
			return;
		}
		const match = dayOptions.find((option: IDayOption) => {
			return option.value === minimumDonationDate;
		});
		setSelectedDayOption(match);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minimumDonationDate]);

	const clearDateSearch = () => {
		setSelectedDayOption(null);
		setMinimumDonationDate('');
		setIsOpen(false);
	};

	const selectDonationType = (option: DonationTypeOption) => {
		setSelectedDonationType(option);
		setMinimumDonationDate('');
		setSelectedDayOption(null);
		// Clear first donation when switching to Not Donated
		if (option === DonationTypeOption.NotDonated) {
			setIsFirstDonation(false);
		}
	};

	const isCustomSelected = selectedDayOption?.value === DateRangeOption.Custom;

	const filteredDayOptions = dayOptions.filter(option => {
		if (option.value === DateRangeOption.Custom && !showCustomRange) {
			return false;
		}
		return true;
	});

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<span className={css(baseStyleSheet.truncateText)}>{dateRangeMessage}</span>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div className={css(styleSheet.donationTypeOptions)}>
					<div className={css(styleSheet.radioButtonRow)}>
						<RadioButton
							checked={selectedDonationType === DonationTypeOption.HasDonated}
							id={DonationTypeOption.HasDonated}
							onChange={() => selectDonationType(DonationTypeOption.HasDonated)}
						>
							<span>{DonationTypeOption.HasDonated}</span>
						</RadioButton>

						<RadioButton
							checked={selectedDonationType === DonationTypeOption.NotDonated}
							id={DonationTypeOption.NotDonated}
							onChange={() => selectDonationType(DonationTypeOption.NotDonated)}
						>
							<span>{DonationTypeOption.NotDonated}</span>
						</RadioButton>
					</div>

					{selectedDonationType === DonationTypeOption.HasDonated && (
						<div className={css(styleSheet.firstDonationCheckbox)}>
							<Checkbox
								checked={isFirstDonation}
								id='first-donation-checkbox'
								onChange={() => setIsFirstDonation(!isFirstDonation)}
							>
								<span>Show first donations only</span>
							</Checkbox>
						</div>
					)}
				</div>

				<div className={css(dropdownStyles.dropdownSeparator)} />

				<div>
					{isCustomSelected ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setMinimumDonationDate('');
								setMaximumDonationDate('');
							}}
							from={minimumDonationDate ? moment(minimumDonationDate).toDate() : null}
							to={maximumDonationDate ? moment(maximumDonationDate).toDate() : null}
							onChange={(from, to) => {
								if (!from || !to) {
									return;
								}
								setMinimumDonationDate(moment(from).format('YYYY-MM-DD'));
								setMaximumDonationDate(moment(to).format('YYYY-MM-DD'));
							}}
							showTextInput
							disableScrollTo
						/>
					) : null}

					{!isCustomSelected &&
						filteredDayOptions.map(dayOption => {
							return (
								<button
									key={`donation-date-range-option-${dayOption.key}`}
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => selectDateRangeOption(dayOption, e)}
									className={css(
										dropdownStyles.option,
										selectedDayOption?.key === dayOption.key ? dropdownStyles.selected : null
									)}
								>
									{dayOption.label}
								</button>
							);
						})}
				</div>

				<div className={css(dropdownStyles.dropdownSeparator)} />

				{minimumDonationDate || isCustomSelected ? (
					<button className={css(baseStyleSheet.ctaButton)} onClick={clearDateSearch}>
						Reset
					</button>
				) : null}
			</div>
		</Dropdown>
	);
};
